// src/pages/admin/SupportAdminPage.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiX, FiPlus, FiTrash2 } from 'react-icons/fi';
import { MdDashboard } from 'react-icons/md';

import { useAuth } from '../../context/AuthContext';
import { useRemoteCss } from '../../hooks/useRemoteCss';
import axiosInstance from '../../api/axiosInstance';

import '../../pages/cx.css';

/* ───────── tiny loader ───────── */
const Loader = () => (
  <>
    <div className="loader-wrapper">
      <div className="loader" />
    </div>
    <style>{`
      .loader-wrapper{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:#f7f7f7;z-index:9999}
      .loader{width:60px;aspect-ratio:1;display:grid}
      .loader:before,.loader:after{content:"";grid-area:1/1;--c:no-repeat radial-gradient(farthest-side,var(--accent, #b89b6e) 92%,#0000);
        background:var(--c)50%0,var(--c)50%100%,var(--c)100%50%,var(--c)0 50%;background-size:14px 14px;animation:l12 1s infinite}
      .loader:before{margin:5px;filter:hue-rotate(15deg);background-size:9px 9px;animation-timing-function:linear}
      @keyframes l12{100%{transform:rotate(.5turn)}}
    `}</style>
  </>
);

const emptyContact = () => ({
  country: '',
  supportNumber: '',
  supportEmail: '',
  contactPerson: '',
  supportHours: '',
  note: '',
});

function normalizeContacts(list) {
  const arr = Array.isArray(list) ? list : [];
  const seen = new Set();
  const out = [];

  for (const raw of arr) {
    const item = { ...emptyContact(), ...(raw || {}) };
    item.country = String(item.country || '').trim();
    if (!item.country) continue;

    const key = item.country.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    item.supportNumber = String(item.supportNumber || '').trim();
    item.supportEmail = String(item.supportEmail || '').trim();
    item.contactPerson = String(item.contactPerson || '').trim();
    item.supportHours = String(item.supportHours || '').trim();
    item.note = String(item.note || '').trim();

    out.push(item);
  }

  // stable ordering (A-Z)
  out.sort((a, b) => a.country.localeCompare(b.country));
  return out;
}

function synthesizeContactsFromLegacy(incoming) {
  const legacyCountries = Array.isArray(incoming?.countriesSupported) ? incoming.countriesSupported : [];
  const countries = legacyCountries.map((c) => String(c || '').trim()).filter(Boolean);

  const base = {
    supportNumber: String(incoming?.supportNumber || '').trim(),
    supportEmail: String(incoming?.supportEmail || '').trim(),
    contactPerson: String(incoming?.contactPerson || '').trim(),
    supportHours: String(incoming?.supportHours || '').trim(),
    note: String(incoming?.note || '').trim(),
  };

  if (!countries.length) return [];
  return normalizeContacts(countries.map((country) => ({ country, ...base })));
}

/**
 * Role normalization:
 * - "SystemAdmin" / "systemadmin" / "System Admin" / "system_admin" -> "systemadmin"
 * - "Admin" -> "admin"
 */
function normRole(r) {
  return String(r || '')
    .toLowerCase()
    .trim()
    .replace(/[\s_-]+/g, ''); // unify spaces/underscores/hyphens
}

/**
 * Admin check:
 * - Prefer a single role string (authData.role) like SystemLogPage
 * - Also support authData.roles array
 * - Optional permission gate if you later add "Support.Edit"
 */
function isAdminLike(authData) {
  const roleStr = normRole(authData?.role);
  if (roleStr === 'systemadmin' || roleStr === 'admin') return true;

  const rolesRaw = Array.isArray(authData?.roles) ? authData.roles : [];
  if (rolesRaw.some((r) => ['systemadmin', 'admin'].includes(normRole(r)))) return true;

  // Optional future-proofing: permission-based admin
  if (Array.isArray(authData?.permissions) && authData.permissions.includes('Support.Edit')) return true;

  return false;
}

export default function SupportAdminPage() {
  const { authData } = useAuth();
  const token = authData?.token;
  const adminOk = useMemo(() => Boolean(token) && isAdminLike(authData), [token, authData]);

  const navigate = useNavigate();

  // Optional remote CSS (lets you override from server)
  const cssUrl = `${axiosInstance.defaults.baseURL}/styles/SupportAdminPage`;
  const cssReady = useRemoteCss(cssUrl);

  const [minDelayDone, setMinDelayDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMinDelayDone(true), 250);
    return () => clearTimeout(t);
  }, []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [contacts, setContacts] = useState([]);
  const [draft, setDraft] = useState([]);

  const [newCountry, setNewCountry] = useState('');

  useEffect(() => {
    document.title = 'Nnetego | Support Admin';
  }, []);

  const fetchSupport = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await axiosInstance.get('/pages/support');
      const incoming = res?.data || {};

      const incomingContacts = normalizeContacts(incoming?.countryContacts);
      const finalContacts = incomingContacts.length ? incomingContacts : synthesizeContactsFromLegacy(incoming);

      setContacts(finalContacts);
      setDraft(finalContacts);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data ||
        e?.message ||
        'Failed to load support details.';
      setError(typeof msg === 'string' ? msg : 'Failed to load support details.');
      setContacts([]);
      setDraft([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSupport();
  }, [fetchSupport]);

  const addCountryContact = () => {
    const country = String(newCountry || '').trim();
    if (!country) return;

    const exists = (draft || []).some((c) => String(c?.country || '').toLowerCase() === country.toLowerCase());
    if (exists) {
      setError(`A contact for "${country}" already exists.`);
      return;
    }

    const next = normalizeContacts([...(draft || []), { ...emptyContact(), country }]);
    setDraft(next);
    setNewCountry('');
    setError(null);
  };

  const updateContact = (idx, patch) => {
    setDraft((prev) => {
      const next = [...(prev || [])];
      const cur = next[idx] || emptyContact();
      next[idx] = { ...cur, ...patch };
      return next;
    });
  };

  const removeContact = (idx) => {
    setDraft((prev) => {
      const next = [...(prev || [])];
      next.splice(idx, 1);
      return next;
    });
  };

  const save = async () => {
    if (!token) {
      setError('Not authenticated.');
      return;
    }
    if (!adminOk) {
      setError('You do not have permission to update support details.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const normalized = normalizeContacts(draft);

      // Backward compatible payload for older APIs:
      // - New: countryContacts + countriesSupported
      // - Legacy: derive old fields from the first country contact
      const first = normalized[0] || emptyContact();

      const payload = {
        countryContacts: normalized,
        countriesSupported: normalized.map((c) => c.country),

        supportNumber: first.supportNumber || '',
        supportEmail: first.supportEmail || '',
        contactPerson: first.contactPerson || '',
        supportHours: first.supportHours || '',
        note: first.note || '',
      };

      await axiosInstance.put('/pages/support', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setContacts(normalized);
      setDraft(normalized);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        setError('You do not have permission to update support details (admin access required).');
      } else {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data ||
          e?.message ||
          'Failed to save support details.';
        setError(typeof msg === 'string' ? msg : 'Failed to save support details.');
      }
    } finally {
      setSaving(false);
    }
  };

  const configuredCountries = useMemo(() => normalizeContacts(draft).map((c) => c.country), [draft]);

  if (!cssReady || !minDelayDone) return <Loader />;

  return (
    <div className="lux-layout">
      <div className="lux-topbar">Support Admin • Country Contacts</div>

      <header className="lux-header support-header">
        <div className="lux-container header-inner">
          <div className="lux-logo" onClick={() => navigate('/')} role="button" tabIndex={0}>
            <img src="/images/logo.png" alt="Nnetego" style={{ height: 28 }} />
          </div>

          <div className="header-right">
            <button className="lux-btn-ghost" onClick={() => navigate(-1)}>
              <FiArrowLeft /> Back
            </button>

            <button className="lux-cta" onClick={() => navigate('/dashboard')}>
              <MdDashboard style={{ marginRight: 8 }} /> Dashboard
            </button>
          </div>
        </div>
      </header>

      <section className="lux-section lux-section--ivory support-wrap">
        <div className="lux-container">
          <div className="support-head">
            <div>
              <h1 className="lux-h2 support-title">Support Admin</h1>
              <p className="lux-copy support-sub">
                Maintain country-specific support contacts. Each country can have its own phone, email, contact person, hours, and notes.
              </p>
            </div>

            <div className="support-actions">
              <button className="lux-btn-ghost" onClick={fetchSupport} disabled={loading || saving}>
                Refresh
              </button>

              <button className="lux-btn-ghost" onClick={() => setDraft(contacts)} disabled={saving}>
                <FiX /> Reset
              </button>

              <button className="lux-cta" onClick={save} disabled={saving || !adminOk}>
                <FiSave /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          {!adminOk && (
            <div className="support-alert">
              You are logged in, but your account does not appear to have admin rights to edit support contacts.
              <div style={{ marginTop: 8, opacity: 0.9 }}>
                Detected role: <strong>{String(authData?.role || '-')}</strong>
              </div>
            </div>
          )}

          {error && <div className="support-alert">{error}</div>}

          <div className="support-grid">
            {/* LEFT: editor */}
            <div className="support-card">
              <div className="support-card-head">
                <div className="support-card-title">Country Support Contacts</div>
                <div className="support-badge">Edit &amp; manage</div>
              </div>

              <div className="edit-add-row">
                <div className="edit-add-label">Add country</div>
                <div className="edit-add-controls">
                  <input
                    className="support-input"
                    value={newCountry}
                    onChange={(e) => setNewCountry(e.target.value)}
                    placeholder="e.g., Botswana"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCountryContact();
                      }
                    }}
                    disabled={!adminOk}
                  />
                  <button
                    className="country-add"
                    type="button"
                    onClick={addCountryContact}
                    disabled={!adminOk || !String(newCountry || '').trim()}
                  >
                    <FiPlus /> Add
                  </button>
                </div>
              </div>

              <div className="support-divider" />

              {draft.length ? (
                <div className="edit-list">
                  {draft.map((c, idx) => (
                    <div key={`${c.country || 'country'}-${idx}`} className="edit-card">
                      <div className="edit-card-head">
                        <div className="edit-card-title">
                          <input
                            className="support-input"
                            value={c.country || ''}
                            onChange={(e) => updateContact(idx, { country: e.target.value })}
                            placeholder="Country name"
                            disabled={!adminOk}
                          />
                        </div>

                        <button
                          className="edit-delete"
                          type="button"
                          onClick={() => removeContact(idx)}
                          title="Remove country"
                          disabled={!adminOk}
                        >
                          <FiTrash2 />
                        </button>
                      </div>

                      <div className="edit-grid">
                        <div className="edit-field">
                          <div className="support-item-label">Support Number</div>
                          <input
                            className="support-input"
                            value={c.supportNumber || ''}
                            onChange={(e) => updateContact(idx, { supportNumber: e.target.value })}
                            placeholder="+267 76 350 228"
                            disabled={!adminOk}
                          />
                        </div>

                        <div className="edit-field">
                          <div className="support-item-label">Support Email</div>
                          <input
                            className="support-input"
                            value={c.supportEmail || ''}
                            onChange={(e) => updateContact(idx, { supportEmail: e.target.value })}
                            placeholder="support@yourdomain.com"
                            disabled={!adminOk}
                          />
                        </div>

                        <div className="edit-field">
                          <div className="support-item-label">Person to Contact</div>
                          <input
                            className="support-input"
                            value={c.contactPerson || ''}
                            onChange={(e) => updateContact(idx, { contactPerson: e.target.value })}
                            placeholder="Support Desk"
                            disabled={!adminOk}
                          />
                        </div>

                        <div className="edit-field">
                          <div className="support-item-label">Support Hours</div>
                          <input
                            className="support-input"
                            value={c.supportHours || ''}
                            onChange={(e) => updateContact(idx, { supportHours: e.target.value })}
                            placeholder="Mon–Fri 08:00–17:00 CAT"
                            disabled={!adminOk}
                          />
                        </div>

                        <div className="edit-field edit-field-full">
                          <div className="support-item-label">Notes</div>
                          <textarea
                            className="support-textarea"
                            value={c.note || ''}
                            onChange={(e) => updateContact(idx, { note: e.target.value })}
                            placeholder="After-hours handling, escalation paths, local office context, etc."
                            rows={3}
                            disabled={!adminOk}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="support-item-muted">Add at least one country (Botswana, Namibia, etc.).</div>
              )}
            </div>

            {/* RIGHT: Guidance */}
            <div className="support-card">
              <div className="support-card-head">
                <div className="support-card-title">Guidance</div>
                <div className="support-badge">How to use</div>
              </div>

              <div className="support-mini">
                <div className="support-mini-title">Operational guidance</div>
                <ul className="support-mini-list">
                  <li>Select the correct country contact details when supporting multi-region customers.</li>
                  <li>Keep phone and email accurate; these are used for escalation and outage response.</li>
                  <li>Use “Notes” to describe after-hours handling, local office context, or escalation paths.</li>
                  <li>If you need different hours by timezone, specify them explicitly (e.g., CAT, WAT).</li>
                </ul>
              </div>

              <div className="support-divider" />

              <div className="support-mini">
                <div className="support-mini-title">Countries configured</div>
                <div className="country-chips">
                  {configuredCountries.length ? (
                    configuredCountries.map((name) => (
                      <span key={name} className="chip view">
                        {name}
                      </span>
                    ))
                  ) : (
                    <div className="support-item-muted">None yet.</div>
                  )}
                </div>
              </div>

              <div className="support-divider" />

              <button className="lux-btn-ghost" onClick={() => navigate('/support')}>
                View public Support page
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="lux-footer">
        <div className="lux-container">
          <div className="lux-legal">
            <div className="lux-legal-copy">
              © {new Date().getFullYear()} Nnetego. All rights reserved. <a href="/legal/privacy">Privacy</a> •{' '}
              <a href="/legal/terms">Terms</a>
            </div>
            <div className="lux-legal-credits">
              Designed by{' '}
              <a href="https://lanesands.com" target="_blank" rel="noopener noreferrer">
                Lanesands
              </a>
              .
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        html, body, #root { height: 100%; }
        body { margin: 0; }
        .lux-layout{ min-height:100vh; display:flex; flex-direction:column; }
        .support-wrap{ flex:1 0 auto; }
        .lux-footer{ margin-top:auto; flex-shrink:0; }

        .lux-topbar{
          position:sticky; top:0; z-index:1300;
          height:32px; display:flex; align-items:center; justify-content:center;
          padding:0 24px; font-size:.76rem; letter-spacing:.18em; text-transform:uppercase;
          color:#f6f2ea; background:rgba(10,8,6,.95);
        }
        .lux-header{
          position:sticky; top:32px; z-index:1290;
          backdrop-filter:blur(22px);
          border-bottom:1px solid rgba(255,255,255,.08);
        }
        .support-header{ box-shadow:0 16px 45px rgba(0,0,0,.12); }
        .header-inner{ display:flex; align-items:center; justify-content:space-between; padding:12px 40px; }
        @media (max-width:768px){ .header-inner{padding:10px 18px;} }

        .lux-cta{
          border:none; border-radius:999px; padding:10px 26px;
          font-size:.86rem; font-weight:600; letter-spacing:.16em; text-transform:uppercase;
          background:linear-gradient(135deg,#b89b6e,#e6cf9d);
          color:#25170a; cursor:pointer;
          display:inline-flex; align-items:center; gap:8px;
          box-shadow:0 16px 38px rgba(0,0,0,.32);
        }
        .lux-btn-ghost{
          border-radius:999px; padding:9px 22px;
          border:1px solid rgba(255,255,255,.76);
          background:rgba(15,11,8,.18);
          color:#f8f4ec; font-size:.84rem; letter-spacing:.12em; text-transform:uppercase;
          cursor:pointer; display:inline-flex; align-items:center; gap:8px;
          backdrop-filter:blur(10px);
        }

        .support-wrap{ padding-top:36px; }
        .support-head{
          display:flex; align-items:flex-start; justify-content:space-between; gap:18px;
          margin-bottom:18px;
        }
        @media (max-width:900px){ .support-head{ flex-direction:column; } }
        .support-title{ margin:0; }
        .support-sub{ margin-top:8px; max-width:760px; }

        .support-actions{ display:flex; gap:10px; flex-wrap:wrap; align-items:center; }

        .support-alert{
          margin:12px 0 18px;
          padding:12px 14px;
          border-radius:14px;
          background:rgba(190, 40, 40, .08);
          border:1px solid rgba(190, 40, 40, .22);
          color:#7b1a1a;
        }

        .support-grid{
          display:grid;
          grid-template-columns: 1.15fr .85fr;
          gap:16px;
          align-items:start;
        }
        @media (max-width: 980px){ .support-grid{ grid-template-columns: 1fr; } }

        .support-card{
          background:var(--card-bg,#fff);
          border:1px solid var(--card-border,#eee);
          border-radius:18px;
          padding:16px 16px 14px;
          box-shadow:0 1px 2px rgba(0,0,0,.03);
        }
        .support-card-head{
          display:flex; align-items:center; justify-content:space-between;
          gap:10px; margin-bottom:12px;
        }
        .support-card-title{
          font-weight:700; letter-spacing:.08em; text-transform:uppercase;
          font-size:.86rem; color:var(--heading-color,#111);
        }
        .support-badge{
          font-size:.74rem;
          letter-spacing:.12em;
          text-transform:uppercase;
          color:var(--accent,#b89b6e);
          background:rgba(184,155,110,.12);
          border:1px solid rgba(184,155,110,.35);
          padding:6px 10px;
          border-radius:999px;
          white-space:nowrap;
        }

        .support-item-label{
          font-size:.76rem;
          letter-spacing:.14em;
          text-transform:uppercase;
          color:#8b7f6c;
          margin-bottom:6px;
        }
        .support-item-muted{ color:var(--copy-color,#6b6b6b); }

        .support-input, .support-textarea{
          width:100%;
          border-radius:12px;
          border:1px solid rgba(0,0,0,.12);
          padding:10px 12px;
          outline:none;
          font-size:.95rem;
          background:#fff;
          color:#1f1a14;
        }
        .support-textarea{ resize:vertical; }
        .support-input:focus, .support-textarea:focus{
          border-color:rgba(184,155,110,.75);
          box-shadow:0 0 0 3px rgba(184,155,110,.18);
        }

        .edit-add-row{ display:flex; flex-direction:column; gap:10px; }
        .edit-add-label{ font-size:.76rem; letter-spacing:.14em; text-transform:uppercase; color:#8b7f6c; }
        .edit-add-controls{ display:flex; gap:10px; align-items:center; }
        @media (max-width:520px){ .edit-add-controls{ flex-direction:column; align-items:stretch; } }

        .country-add{
          border-radius:999px;
          border:1px solid rgba(184,155,110,.55);
          background:rgba(184,155,110,.12);
          color:#3b2b16;
          padding:10px 14px;
          cursor:pointer;
          display:inline-flex; align-items:center; gap:8px;
          font-weight:700;
        }
        .country-add:disabled{ opacity:.55; cursor:not-allowed; }

        .edit-list{ display:flex; flex-direction:column; gap:14px; }
        .edit-card{
          border:1px solid rgba(0,0,0,.06);
          background:rgba(0,0,0,.01);
          border-radius:16px;
          padding:12px;
        }
        .edit-card-head{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          margin-bottom:10px;
        }
        .edit-card-title{ flex:1; }

        .edit-delete{
          border:1px solid rgba(190,40,40,.25);
          background:rgba(190,40,40,.08);
          color:#7b1a1a;
          width:42px; height:42px;
          border-radius:14px;
          cursor:pointer;
          display:grid; place-items:center;
        }
        .edit-delete:disabled{ opacity:.5; cursor:not-allowed; }
        .edit-delete:hover{ background:rgba(190,40,40,.12); }

        .edit-grid{
          display:grid;
          grid-template-columns: 1fr 1fr;
          gap:12px;
        }
        @media (max-width:820px){ .edit-grid{ grid-template-columns:1fr; } }
        .edit-field-full{ grid-column:1 / -1; }

        .support-divider{ height:1px; background:rgba(0,0,0,.06); margin:16px 0; }

        .support-mini-title{
          font-weight:700; letter-spacing:.08em; text-transform:uppercase;
          font-size:.86rem; color:var(--heading-color,#111);
          margin-bottom:8px;
        }
        .support-mini-list{
          margin:0; padding-left:18px;
          color:var(--copy-color,#534c42);
          line-height:1.6;
        }

        .country-chips{ display:flex; flex-wrap:wrap; gap:8px; margin-top:10px; }
        .chip{
          display:inline-flex; align-items:center; gap:8px;
          padding:8px 12px;
          border-radius:999px;
          border:1px solid rgba(0,0,0,.08);
          background:rgba(0,0,0,.02);
          color:#2a241c;
          font-weight:600;
        }
        .chip.view{
          border-color:rgba(184,155,110,.35);
          background:rgba(184,155,110,.10);
        }
      `}</style>
    </div>
  );
}
